import { useState, useEffect, useCallback } from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';
import useActiveUser from '../collaboration/useActiveUser';
import OpenAI from 'openai';
import { useTranslation } from 'react-i18next';

export interface Message {
  role: 'user' | 'assistant';
  content: Array<{
    type: string;
    text?: string | { value: string };
    image_url?: {
      url: string;
    };
    floness_interpretation?: {
      content: string;
      description: string;
      totalIncludingVAT: number;
      date: string;
    };
  }>;
  createdAt: number;
  metadata?: {
    organization_id?: string;
    user_name?: string;
    user_email?: string;
    user_id?: string;
  };
}

interface Chat {
  messages: Message[];
  run: any;
  runListeners: ((run: any) => void)[];
  listeners: ((messages: Message[]) => void)[];
  lastRefresh?: number;
  refreshTimer?: ReturnType<typeof setInterval> | null;
  backgroundRefreshTimer?: ReturnType<typeof setInterval> | null;
  interval?: number;
}

const chats: Record<string, Chat> = {};

const useChat = ({ threadId }: { threadId?: string }) => {
  const { t } = useTranslation();
  const [activeOrganisation] = useActiveOrganisation();
  const { activeUser } = useActiveUser();
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeRun, setActiveRun] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [nextResponder, setNextResponder] = useState<string | null>(null);
  const [isLoadingInitialMessages, setIsLoadingInitialMessages] =
    useState(false);
  const [initialFetchError, setInitialFetchError] = useState<string | null>(
    null
  );

  const getOpenAIClient = async () => {
    const baseURL = import.meta.env.VITE_FLONESS_OAI_BASE_URL;
    const tokenResponse = await fetch('/access-token', {
      headers: {
        'x-organization': activeOrganisation.organizationId,
      },
    });
    console.log('tokenResponse: ', tokenResponse);
    const { accessToken } = await tokenResponse.json();
    return new OpenAI({
      apiKey: accessToken,
      dangerouslyAllowBrowser: true,
      baseURL,
    });
  };

  const startPolling = (threadId: string) => {
    // Clear any existing active polling
    if (chats[threadId].refreshTimer) {
      clearInterval(chats[threadId].refreshTimer);
    }

    // Start new frequent polling for active runs
    chats[threadId].refreshTimer = setInterval(() => {
      fetchMessages(threadId);
    }, chats[threadId].interval || 1000);
  };

  useEffect(() => {
    if (!threadId) {
      // Don't set error when threadId is undefined - this is normal when no document is selected
      return;
    }

    try {
      if (!chats[threadId]) {
        chats[threadId] = {
          messages: [],
          listeners: [],
          run: null,
          runListeners: [],
          interval: 1000,
        };
      }

      chats[threadId].runListeners.push(setActiveRun);
      chats[threadId].listeners.push(setMessages);
      setIsInitialized(true);

      // Initial fetch of messages with isInitialFetch flag
      fetchMessages(threadId, true);

      // Start background refresh for new messages from other clients
      const backgroundRefreshTimer = setInterval(() => {
        // Only fetch if there's no active polling
        if (!chats[threadId].refreshTimer) {
          fetchMessages(threadId);
        }
      }, 10000); // Check every 10 seconds

      // Store the background timer reference
      chats[threadId].backgroundRefreshTimer = backgroundRefreshTimer;

      return () => {
        // Cleanup listeners
        chats[threadId].runListeners = chats[threadId].runListeners.filter(
          (listener) => listener !== setActiveRun
        );
        chats[threadId].listeners = chats[threadId].listeners.filter(
          (listener) => listener !== setMessages
        );

        // Clear both timers
        if (chats[threadId].refreshTimer) {
          clearInterval(chats[threadId].refreshTimer);
          chats[threadId].refreshTimer = null;
        }
        if (chats[threadId].backgroundRefreshTimer) {
          clearInterval(chats[threadId].backgroundRefreshTimer);
          chats[threadId].backgroundRefreshTimer = null;
        }
      };
    } catch (err) {
      console.error('Error initializing chat:', err);
      setError(t('Failed to initialize chat'));
    }
  }, [threadId]);

  const fetchMessages = async (threadId: string, isInitialFetch = false) => {
    try {
      if (isInitialFetch) {
        setIsLoadingInitialMessages(true);
        setInitialFetchError(null);
      }

      const openai = await getOpenAIClient();
      const messagesList = await openai.beta.threads.messages.list(threadId);
      const messages = messagesList.data || [];

      const messagesFiltered = messagesList.data.filter(
        (message) =>
          (message.internal_role == 'chatbot' ||
            message.internal_role == 'accountant' ||
            message.internal_role == 'entrepreneur' ||
            message.internal_role == 'tool summary' ||
            message.internal_role == 'analysis summary') &&
          message.subtype !== 'tool call' &&
          message.subtype !== 'tool result'
      );

      // Consider empty messages as an error during initial fetch
      if (isInitialFetch && messages.length === 0) {
        throw new Error(t('No messages found in this chat'));
      }

      chats[threadId].messages = messagesFiltered;
      chats[threadId].listeners.forEach((listener) =>
        listener(messagesFiltered)
      );

      // Take the next responder from the last message
      const nextResponder =
        messagesFiltered[messagesFiltered.length - 1].next_responder;
      setNextResponder(nextResponder);

      // If we have an active run, check its status
      if (chats[threadId].run?.id) {
        const run = await openai.beta.threads.runs.retrieve(
          threadId,
          chats[threadId].run.id
        );

        // Update loading state based on run status
        setIsWaitingForResponse(
          run.status !== 'completed' &&
            run.status !== 'failed' &&
            run.status !== 'cancelled' &&
            !!run.status
        );

        // Stop polling if the run is completed or failed
        if (
          run.status === 'completed' ||
          run.status === 'failed' ||
          run.status === 'cancelled' ||
          !run.status
        ) {
          if (chats[threadId].refreshTimer) {
            clearInterval(chats[threadId].refreshTimer);
            chats[threadId].refreshTimer = null;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setIsWaitingForResponse(false);
      setNextResponder(null);
      if (chats[threadId].refreshTimer) {
        clearInterval(chats[threadId].refreshTimer);
        chats[threadId].refreshTimer = null;
      }
      if (isInitialFetch) {
        setInitialFetchError(
          error instanceof Error
            ? error.message
            : t('Failed to load chat history')
        );
      }
    } finally {
      if (isInitialFetch) {
        setIsLoadingInitialMessages(false);
      }
    }
  };

  const addMessage = useCallback(
    async (message: Message) => {
      try {
        // Add check to prevent sending message while waiting for response
        if (isWaitingForResponse) {
          console.warn('Cannot send message while waiting for response');
          return false;
        }

        setMessages((prev) => [...prev, message]);
        setIsWaitingForResponse(true);

        const openai = await getOpenAIClient();

        const createdMessage = await openai.beta.threads.messages.create(
          threadId,
          {
            // Always set role to user; back-end will set the correct internal role
            role: 'user',
            content: message.content,
            metadata: {
              user_id: activeUser?.id,
              user_name: activeUser?.name,
              user_email: activeUser?.email,
              organization_id: activeOrganisation.organizationId,
            },
          }
        );

        const nextResponder = createdMessage.next_responder;

        if (nextResponder === 'assistant') {
          // Create and start a run after sending the message
          const thread = await openai.beta.threads.retrieve(threadId);
          const run = await openai.beta.threads.runs.create(threadId, {
            assistant_id: thread.metadata.assistantId,
            stream: false,
            metadata: {
              user_id: activeUser?.id,
              user_name: activeUser?.name,
              user_email: activeUser?.email,
              organization_id: activeOrganisation.organizationId,
            },
          });
          chats[threadId].run = {
            id: run.id, // Make sure we're storing the correct run ID
            status: run.status,
          };
          chats[threadId].runListeners.forEach((listener) => listener(run));
        } else {
          setIsWaitingForResponse(false);
          setNextResponder(nextResponder);
        }

        // Start polling for new messages
        startPolling(threadId);
      } catch (err) {
        console.error('Error in chat:', err);
        setIsWaitingForResponse(false);
        setError(
          err instanceof Error ? err.message : t('An unexpected error occurred')
        );
        return false;
      }
      return true;
    },
    [
      threadId,
      activeOrganisation,
      activeUser,
      isWaitingForResponse,
      nextResponder,
    ]
  );

  const retryInitialFetch = useCallback(async () => {
    if (!threadId) return;
    try {
      setIsInitialized(false);
      setError(null);
      setInitialFetchError(null);
      await fetchMessages(threadId, true);
      setIsInitialized(true);
    } catch (err) {
      console.error('Error retrying initial fetch:', err);
      setError(t('Failed to initialize chat'));
    }
  }, [threadId]);

  return {
    messages,
    addMessage,
    run: activeRun,
    error,
    isInitialized,
    isWaitingForResponse,
    nextResponder,
    isLoadingInitialMessages,
    initialFetchError,
    retryInitialFetch,
  };
};

export default useChat;
